import React, {
    useState,
    useEffect,
    useCallback
} from 'react';

import {
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import {
    useIsExporting
} from '../../../hooks/useExport.js';

import {
    useGetDashboardType,
    DashboardTypes
} from '../hooks/useGetDashboardType.js';

import {
    useShowInstantSearchTour
} from '../hooks/useShowTours.js';

import RIQModal from '../../../components/RIQModal.jsx';


const MODAL_DISPLAY_TIME_MS = 3000; // 3 seconds
const MAX_PILLS_TO_SHOW = 3;

export function useSetShowInstantSearchModal() {

    // This hook uses a global variable attached to window to track whether the modal should be shown because we don't want to show this modal when you're
    // coming back from editing a query or viewing details mode. But, the first load the page or the first load after visiting the NoSearchResults page should
    // result in the modal popping up so we need to track some state across react instances.
    const [modalEligibleToShow, setShowModalState] = useState(_.isUndefined(window.__showInstantSearchModal) ? true : window.__showInstantSearchModal);

    const markModalEligibleToShow = useCallback((showModal) => {
        setShowModalState(showModal);
        window.__showInstantSearchModal = showModal;
    }, [setShowModalState]);

    return [modalEligibleToShow, markModalEligibleToShow];
}


export function InstantSearchInfoModal({isAnyWidgetLoaded}) {
    const isExporting = useIsExporting();
    const dashboardType = useGetDashboardType();

    const searchTerms = useCurationConfigValue('searchTerms');
    const isInstantSearchDashboard = !isExporting && dashboardType === DashboardTypes.INSTANT_SEARCH;

    const [modalEligibleToShow, markModalEligibleToShow] = useSetShowInstantSearchModal();
    const [showModal, setShowModal] = useState(isInstantSearchDashboard && modalEligibleToShow);
    const [modalShownForMinimumTime, setModalShownForMinimumTime] = useState(false);

    useEffect(() => {
        if (showModal && !modalShownForMinimumTime) {
            setTimeout(() => {
                setModalShownForMinimumTime(true);
            }, MODAL_DISPLAY_TIME_MS);
        }
    }, [showModal, modalShownForMinimumTime, setModalShownForMinimumTime]);

    useEffect(() => {
        if (isInstantSearchDashboard && modalEligibleToShow) {
            markModalEligibleToShow(false);
            setShowModal(true);
        } else if (showModal && modalShownForMinimumTime && isAnyWidgetLoaded) {
            setShowModal(false);
        }
    }, [isInstantSearchDashboard, modalEligibleToShow, showModal, modalShownForMinimumTime, markModalEligibleToShow, isAnyWidgetLoaded, setShowModal]);

    useShowInstantSearchTour(isInstantSearchDashboard && isAnyWidgetLoaded && !showModal);

    let numTruncatedPills = 0;
    // The +1 here is to prevent a situation where we'd show "+1 others" (which would be silly since we could just show the term in that case).
    if (_.size(searchTerms) > MAX_PILLS_TO_SHOW + 1) {
        numTruncatedPills = _.size(searchTerms) - MAX_PILLS_TO_SHOW;
    }
    let pillsToShow = _.take(searchTerms, _.size(searchTerms) - numTruncatedPills);
    if (numTruncatedPills) {
        pillsToShow = [...pillsToShow, `${numTruncatedPills} others`];
    }

    return (
        <RIQModal
            showModal={showModal}
            variant='small'
        >
            <div className='tlw-m-15 tlw-grid tlw-place-items-center tlw-gap-5'>
                <div className='tlw-text-[36px] tlw-text-gray-500'>
                    <span className='fa fa-circle-o-notch fa-spin'></span>
                </div>

                <div className='tlw-mt-5 tlw-text-md tlw-font-bold'>
                    Generating Instant Search results for
                </div>

                <div>
                    {_.map(pillsToShow, (term, idx) => {
                        let pillClassName = 'tlw-inline-flex tlw-items-center before_tlw-block before_tlw-pr-3';
                        if (idx !== 0) {
                            pillClassName = `${pillClassName} before_tlw-text-xs before_tlw-text-gray-650 before_tlw-content-[attr(data-operator)]`;
                        }

                        if (idx === pillsToShow.length - 1 && numTruncatedPills) {
                            pillClassName = `${pillClassName} tlw-text-gray-500`;
                        }

                        return (
                            <div
                                key={idx}
                                className={pillClassName}
                                data-operator='OR'
                            >
                                <div
                                    className='tlw-mb-2 tlw-mr-2 tlw-inline-block tlw-max-w-[100px] tlw-items-center tlw-rounded-[15px] tlw-bg-gray-200 tlw-px-5 tlw-py-1'
                                >
                                    <div
                                        className='tlw-w-full tlw-overflow-hidden tlw-text-ellipsis tlw-whitespace-nowrap'
                                    >
                                        { term }
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className='tlw-mt-10 tlw-text-md'>
                    Click the <span className='fa fa-wrench tlw-ml-1'></span> edit button to refine the terms to broaden or narrow your search.
                </div>
            </div>
        </RIQModal>
    );
}
